import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    Animated,
    Modal,
    ScrollView,
    TouchableOpacity,
    useWindowDimensions,
    View,
} from 'react-native';

import { createStyleSheet, useStyles } from 'react-native-unistyles';

import CustomButton, { ButtonTypes } from '../CustomButton';
import { translate } from '../../services/translate';

import { faBarsSort, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import MyAppText from '../MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
    FieldValues,
    FormProvider,
    Path,
    PathValue,
    useFormContext,
    UseFormReturn,
    useWatch,
    get,
} from 'react-hook-form';
import CollapsibleFilter from '../Filters/Dispatch/CollapsibleFilter';


function FilterSectionComponent({ label }: { label: string; }) {
    const { styles } = useStyles(stylesheet);

    return (
        <MyAppText style={styles.sectionTitle}>
            {label}
        </MyAppText>
    );
}


/**
 * Props received by every dynamically rendered filter component.
 *
 * TName is the exact field name represented by that filter.
 */
export type FilterComponentProps<TName extends string> = {
    name: TName;
};


/**
 * Base definition shared by every filter.
 */
type BaseFilterDefinition<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>,
> = {
    name: TName;

    // component: FilterComponent<TFieldValues, TName>;
};


/**
 * A filter that is rendered as a collapsible filter.
 *
 * PathValue<TFieldValues, TName> makes value automatically become the
 * actual type of the RHF field referenced by name.
 *
 * Example:
 *
 * type FiltersForm = {
 *     status: 'all' | 'onlyOffline' | 'onlyClosed';
 * };
 *
 * name: 'status'
 *
 * => value is inferred as:
 *    'all' | 'onlyOffline' | 'onlyClosed'
 */

type FilterComponent<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>,
> = React.ComponentType<FilterComponentProps<TName>> & {
    getCount: (
        value: PathValue<TFieldValues, TName>,
    ) => number;
};

export type CollapsibleFilterDefinition<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>,
> = BaseFilterDefinition<TFieldValues, TName> & {
    componentHandlesCount: false;
    label: string;
    omit?: boolean;
    cleans?: Path<TFieldValues>[];

    getCount: (value: PathValue<TFieldValues, TName>) => number;
    component: React.ComponentType<FilterComponentProps<TName>>;

};


/**
 * A filter that does not use the collapsible wrapper.
 */
export type SimpleFilterDefinition<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>,
> = BaseFilterDefinition<TFieldValues, TName> & {
    componentHandlesCount: true;
    label: string;
    omit?: boolean;
    cleans?: Path<TFieldValues>[];

    component: FilterComponent<TFieldValues, TName>;
};


/**
 * A single filter definition.
 */
export type FilterDefinition<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>,
> =
    | CollapsibleFilterDefinition<TFieldValues, TName>
    | SimpleFilterDefinition<TFieldValues, TName>;


/**
 * A section contains a union of definitions for every valid form path.
 *
 * This is important: it preserves the relationship between `name` and the
 * corresponding field value type.
 */
type FilterDefinitionForForm<TFieldValues extends FieldValues> = {
    [TName in Path<TFieldValues>]: FilterDefinition<TFieldValues, TName>;
}[Path<TFieldValues>];


export type FilterSection<TFieldValues extends FieldValues> = {
    label: string;
    filters: FilterDefinitionForForm<TFieldValues>[];
};


type Props<TFieldValues extends FieldValues> = {
    cleanValues: TFieldValues;
    form: UseFormReturn<TFieldValues>;
    filters: FilterSection<TFieldValues>[];

    onFilters: (values: TFieldValues) => void;

    onStatusChange?: (isOpen: boolean) => void;

    filterIsLoading?: boolean;
    applyIsLoading?: boolean;
    applyDisabled?: boolean;
    cleanDisabled?: boolean;

    applyText?: string;
    cancelText?: string;

    type?: ButtonTypes;

    filterCount?: number;

    btnContent?: React.ReactNode;

    headerTitle?: string;
    subheaderTitle?: string;

    primaryButton?: boolean;
};

type CleanableFilter<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>,
> = {
    name: TName;
    cleans?: Path<TFieldValues>[];
};

function useFilterCleans<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>,
>(
    form: UseFormReturn<TFieldValues>,
    filter: CleanableFilter<TFieldValues, TName>,
    cleanValues: TFieldValues,
) {
    const value = useWatch({
        control: form.control,
        name: filter.name,
    });

    const previousValue = useRef(value);

    useEffect(() => {
        if (Object.is(previousValue.current, value)) {
            return;
        }

        previousValue.current = value;

        for (const field of filter.cleans ?? []) {
            form.setValue(
                field,
                get(cleanValues, field) as PathValue<
                    TFieldValues,
                    typeof field
                >,
            );
        }
    }, [value, filter.cleans, cleanValues, form]);
}


export interface FiltersRef {
    open: () => void;
    close: () => void;
    toggle: () => void;
}

function getFilterCount<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>,
>(filter: FilterDefinition<TFieldValues, TName>, value: PathValue<TFieldValues, TName>) {
    const count = !filter.componentHandlesCount ? filter.getCount(value) : filter.component.getCount(value);
    return count;
}

function FilterCollapsibleRenderer<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>,

>({ filter, openFilter, toggleFilter, cleanValues }: {
    filter: SimpleFilterDefinition<TFieldValues, TName> | CollapsibleFilterDefinition<TFieldValues, TName>;
    openFilter: string | null;
    toggleFilter: (value: string) => void;
    cleanValues: TFieldValues;

}) {
    const form = useFormContext<TFieldValues>();

    useFilterCleans(form, filter, cleanValues);

    const value = useWatch({
        control: form.control,
        name: filter.name,
    });

    const Component = filter.component;

    return (
        <CollapsibleFilter
            label={filter.label}
            isOpen={openFilter === filter.name}
            onToggle={() => toggleFilter(filter.name)}
            count={!filter.componentHandlesCount ? filter.getCount(value) : filter.component.getCount(value)}
        >
            <Component name={filter.name} />
        </CollapsibleFilter>
    );
}

function FilterModalInner<TFieldValues extends FieldValues>(
    {
        form,
        filters,
        onFilters,
        cleanValues,
        onStatusChange,
        applyIsLoading,
        applyDisabled,
        cleanDisabled,
        btnContent,
        headerTitle,
        subheaderTitle,
        applyText,
        cancelText,
        primaryButton,
    }: Props<TFieldValues>,
    ref: React.ForwardedRef<FiltersRef>,
) {
    const { styles, breakpoint, theme } = useStyles(stylesheet);

    const { width, height } = useWindowDimensions();


    const formValues = useWatch({
        control: form.control,
    });

    const filterCount = useMemo(() => {
        return filters
            .flatMap(section => section.filters)
            .reduce((total, filter) => {

                if (filter.omit) {
                    return total;
                }

                const value = form.getValues(filter.name);

                const count = getFilterCount(filter, value);

                return total + (count > 0 ? 1 : 0);
            }, 0);
    }, [filters, formValues]);

    const sidebarWidth = useMemo(() => {
        if (breakpoint === 'xs') {
            return width;
        }

        return 460;
    }, [width, breakpoint]);

    const [isOpen, setIsOpen] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);

    useImperativeHandle(
        ref,
        () => ({
            open: () => setIsOpen(true),
            close: () => setIsOpen(false),
            toggle: () => setIsOpen(prev => !prev),
        }),
        [],
    );

    const animationSidebar = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(animationSidebar, {
            toValue: isOpen ? sidebarWidth : 0,
            duration: 200,
            useNativeDriver: true,
        }).start();

        onStatusChange?.(isOpen);
    }, [isOpen, sidebarWidth, onStatusChange]);

    const snapshotRef = useRef<TFieldValues | null>(null);

    useEffect(() => {
        if (isOpen) {
            setIsModalVisible(true);
            snapshotRef.current = structuredClone(form.getValues());
        } else {
            const timeout = setTimeout(() => {
                setIsModalVisible(false);
            }, 200);

            return () => clearTimeout(timeout);
        }
    }, [isOpen]);

    const handleApplyFilters = form.handleSubmit(values => {
        onFilters(values);
        setIsOpen(false);
    });

    const [openFilter, setOpenFilter] = useState<string | null>(null);

    const toggleFilter = (name: string) => {
        setOpenFilter(current =>
            current === name ? null : name
        );
    };

    return (
        <FormProvider {...form}>
            {/* TODO: isso aqui poderia ir pro custom button??? */}
            <TouchableOpacity
                style={
                    primaryButton
                        ? styles.filterButtonPrimary
                        : styles.filterButton
                }
                onPress={() => setIsOpen(prev => !prev)}
            >
                <View style={styles.filterButtonContent}>
                    {btnContent || (
                        <>
                            <FontAwesomeIcon
                                icon={faBarsSort as IconDefinition}
                                color={theme.colors.secondaryButton.text}
                                fontSize={16}
                            />

                            <MyAppText
                                style={
                                    primaryButton
                                        ? styles.filterButtonTextPrimary
                                        : styles.filterButtonText
                                }
                            >
                                {translate('filters')}
                            </MyAppText>

                            {filterCount > 0 && (
                                <View style={styles.badge}>
                                    <MyAppText style={styles.badgeText}>
                                        {filterCount}
                                    </MyAppText>
                                </View>
                            )}
                        </>
                    )}
                </View>
            </TouchableOpacity>

            <Modal
                transparent={true}
                visible={isModalVisible}
                animationType="fade"
                onRequestClose={() => {
                    if (snapshotRef.current) {
                        form.reset(snapshotRef.current);
                    }
                    setIsOpen(false);
                }}
            >
                <View
                    style={{
                        backgroundColor: '#31313199',
                        flex: 1,
                    }}
                >
                    <Animated.View
                        style={[
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            { position: 'fixed' },
                            styles.container(height),
                            {
                                width: animationSidebar,
                                borderWidth: isOpen ? 1 : 0,
                            },
                        ]}
                    >
                        {isOpen && (
                            <>
                                <View style={styles.header}>
                                    <View style={styles.headerTopRow}>
                                        <View
                                            style={{
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                gap: 12,
                                            }}
                                        >
                                            <MyAppText
                                                style={{
                                                    fontSize: 18,
                                                    fontWeight: '600',
                                                }}
                                            >
                                                {headerTitle ||
                                                    translate('allFilters')}
                                            </MyAppText>

                                            {filterCount > 0 && (
                                                <View style={styles.badge}>
                                                    <MyAppText
                                                        style={styles.badgeText}
                                                    >
                                                        {filterCount}
                                                    </MyAppText>
                                                </View>
                                            )}
                                        </View>

                                        <TouchableOpacity
                                            onPress={() => {
                                                setIsOpen(false);
                                                if (snapshotRef.current) {
                                                    form.reset(snapshotRef.current);
                                                }
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={
                                                    faXmark as IconDefinition
                                                }
                                                color={theme.colors.iconColor}
                                                fontSize={24}
                                            />
                                        </TouchableOpacity>
                                    </View>

                                    <MyAppText style={styles.headerSubtitle}>
                                        {subheaderTitle ||
                                            translate(
                                                'selectFiltersToApply',
                                            )}
                                    </MyAppText>
                                </View>

                                <View style={styles.filterContainer}>
                                    <View style={styles.inputsHeight}>
                                        <ScrollView
                                            showsVerticalScrollIndicator={false}
                                            contentContainerStyle={styles.scrollContent}
                                            style={{ scrollbarGutter: 'stable' } as object}
                                        >
                                            {filters.map(section => {

                                                if (!section.filters.some(filter => !filter.omit)) {
                                                    return null;
                                                }

                                                return <View key={section.label} style={styles.children}>

                                                    <FilterSectionComponent
                                                        label={section.label}
                                                    />

                                                    {section.filters.map(filter => {

                                                        if (filter.omit) {
                                                            return null;
                                                        }

                                                        return (
                                                            <FilterCollapsibleRenderer
                                                                key={filter.name}
                                                                filter={filter}
                                                                cleanValues={cleanValues}
                                                                openFilter={openFilter}
                                                                toggleFilter={toggleFilter}
                                                            />
                                                        );

                                                    })}
                                                </View>;
                                            })}
                                        </ScrollView>
                                    </View>

                                    <View style={styles.buttonsContainer}>
                                        <CustomButton
                                            contentContainerStyle={{
                                                flex: 1,
                                            }}
                                            text={
                                                cancelText ||
                                                translate('cleanFilters')
                                            }
                                            onPress={() => form.reset(cleanValues)}
                                            type="secondary"
                                            disabled={cleanDisabled}
                                        />

                                        <CustomButton
                                            contentContainerStyle={{
                                                flex: 1,
                                            }}
                                            style={{
                                                paddingHorizontal: 0,
                                            }}
                                            text={
                                                applyText ||
                                                translate('filterResults')
                                            }
                                            onPress={handleApplyFilters}
                                            disabled={applyDisabled}
                                            isLoading={applyIsLoading}
                                        />
                                    </View>
                                </View>
                            </>
                        )}
                    </Animated.View>
                </View>
            </Modal>
        </FormProvider>
    );
}


/**
 * React's ForwardRefExoticComponent cannot preserve the generic parameter
 * of FilterModalInner by itself. This cast keeps FilterModal generic at the
 * call site, allowing TFieldValues to be inferred from `form`.
 */
const FilterModal = forwardRef(FilterModalInner) as <
    TFieldValues extends FieldValues,
>(
    props: Props<TFieldValues> & {
        ref?: React.Ref<FiltersRef>;
    },
) => React.ReactElement | null;


const stylesheet = createStyleSheet(theme => ({
    container: (height: number) => {
        return {
            backgroundColor: theme.colors.container.background,
            top: 0,
            height,
            right: 0,
            zIndex: 10,
            margin: 0,
            borderWidth: 0,
            borderColor: 'transparent',
            padding: 32,
            gap: 40,
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
        };
    },

    header: {
        flexDirection: 'column',
        gap: 4,
    },

    headerTopRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },

    headerSubtitle: {
        fontSize: 16,
        color: theme.colors.neutral['400'],
        fontWeight: '600',
    },

    filterContainer: {
        flex: 1,
        gap: 40,
    },

    inputsHeight: {
        flex: 1,
    },

    buttonsContainer: {
        flexDirection: 'row',
        gap: 24,
    },

    children: {
        zIndex: 2,
        gap: 16,
    },

    scrollContent: {
        flex: 1,
        gap: 20,
        // justifyContent: 'space-between',
    },

    filterButtonPrimary: {
        borderWidth: 0,
        borderColor: undefined,
        backgroundColor: theme.colors.primaryButton.background,
        borderRadius: 5,
        paddingHorizontal: 20,
        height: 40,
        justifyContent: 'center',
    },

    filterButton: {
        borderWidth: 1,
        borderColor: theme.colors.secondaryButton.borderColor,
        backgroundColor: theme.colors.secondaryButton.background,
        borderRadius: 5,
        paddingHorizontal: 20,
        height: 40,
        justifyContent: 'center',
    },

    filterButtonContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },

    filterButtonTextPrimary: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.primaryButton.text,
    },

    filterButtonText: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.secondaryButton.text,
    },

    badge: {
        backgroundColor: theme.colors.primary['500'],
        borderRadius: 4,
        paddingHorizontal: 6,
        paddingVertical: 2,
        minWidth: 22,
        alignItems: 'center',
        justifyContent: 'center',
    },

    badgeText: {
        color: theme.colors.white['500'],
        fontSize: 14,
        fontWeight: 'bold',
    },

    sectionTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.primary['500'],
        textTransform: 'uppercase',
    },
}));


export default FilterModal;
